<script setup lang="ts">
import { useUserStoreHook } from '@/store/modules/user'
import resourceUrl from '@/config/resource'
import IntegardlSide from './IntegardlSide.vue'
import integralApi from '@/api/integralApi'
import userApi from '@/api/userApi'
import { Code } from '@/config/httpCode'
import PayDialog from './PayDialog.vue'
import { PayChannel } from '@/config/constants'
import { debounce } from '@/utils/commonUtils'
import { message } from '@/utils/message'
import CustomList from './CustomList.vue'
import CustomIcon1 from '@/assets/img/custom1.png'
import CustomIcon2 from '@/assets/img/custom2.png'
import CustomIcon3 from '@/assets/img/custom3.png'
import CustomIcon4 from '@/assets/img/custom4.png'
import { CustomType } from '@/config/constants'
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
})
type IntegralRechargeItem = {
  name: string
  description: string
  price: number
  packId: string
  products: {
    product: string
    count: number
  }[]
  minPurchaseCount: number
  unit: boolean
}
const emit = defineEmits(['update:visible', 'close'])
const dialogRechargeVisible = ref(props.visible)
const loading = ref<boolean>(false)
const showMask = ref<boolean>(false)
const payDialogVisible = ref<boolean>(false)
const payType = ref<number>(1)
const customList = ref<any[]>([])
const payInfo = ref<ReqCreateOrder>({
  packId: '',
  purchaseCount: 1,
  channel: PayChannel.WEIXIN,
  qrCode: '',
  orderId: '',
  price: 0,
})
// 金额积分比例
const countRatio = ref<number>(100)
// 使用导出的方法
const avatarUrl = computed(() => resourceUrl.getImageUrl('/assets/temp/default_avatr.png'))

const integralRecharge = ref<IntegralRechargeItem[]>([])
const priceForm = ref({
  price: 0,
})

// 自定义套餐
const customPack = ref<IntegralRechargeItem | null>(null)
// 自定义充值显示名称
const customName = ref('')

// 基于输入金额计算积分数量
const calculateCredits = (price: number) => {
  return price * countRatio.value
}
const changePayType = (type: number) => {
  payType.value = type
}

// 内容切换动画钩子
const onBeforeEnter = (el: Element) => {
  const element = el as HTMLElement
  // 初始状态：完全透明，向右偏移，高度为0
  element.style.opacity = '0'
  element.style.transform = 'translateX(20px)'
  element.style.height = '0'
  element.style.overflow = 'hidden'
  element.style.transition = 'none'
  element.style.willChange = 'transform, opacity, height'
}

const onEnter = (el: Element, done: () => void) => {
  const element = el as HTMLElement

  // 先让内容渲染但不可见
  element.style.opacity = '0'
  element.style.transform = 'translateX(20px)'
  element.style.height = 'auto'
  element.style.overflow = 'visible'

  // 获取实际高度
  const height = element.scrollHeight
  element.style.height = '0'
  element.style.overflow = 'hidden'

  // 强制浏览器重排
  element.offsetHeight

  // 设置过渡并开始动画
  element.style.transition = 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)'

  // 使用双重 requestAnimationFrame 确保动画流畅
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      element.style.height = height + 'px'
      element.style.opacity = '1'
      element.style.transform = 'translateX(0)'

      // 动画完成后清理样式
      setTimeout(() => {
        element.style.height = 'auto'
        element.style.overflow = 'visible'
        element.style.willChange = 'auto'
        done()
      }, 400)
    })
  })
}

const onLeave = (el: Element, done: () => void) => {
  const element = el as HTMLElement
  const height = element.scrollHeight

  // 设置离开动画的初始状态
  element.style.height = height + 'px'
  element.style.overflow = 'hidden'
  element.style.transition = 'all 0.3s cubic-bezier(0.25, 1, 0.5, 1)'
  element.style.willChange = 'transform, opacity, height'

  // 使用双重 requestAnimationFrame 确保动画流畅
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      element.style.height = '0'
      element.style.opacity = '0'
      element.style.transform = 'translateX(-20px)'

      setTimeout(() => {
        element.style.willChange = 'auto'
        done()
      }, 300)
    })
  })
}
// 更新自定义充值套餐名称
const updateCustomName = (price: number | string) => {
  const priceNum = Number(price)
  if (priceNum > 0) {
    const credits = calculateCredits(priceNum)
    customName.value = `${credits}积分`
  } else {
    // 恢复为默认名称
    customName.value = customPack.value?.name || ''
  }
}

const rulesLocalPrice: FormRules['price'] = [
  {
    required: true,
    message: '请输入充值金额',
    trigger: 'blur',
  },
  {
    validator: (rule, value, callback) => {
      const num = Number(value)
      const minAmount = customPack.value?.minPurchaseCount || 10

      if (isNaN(num) || num <= 0 || num % 1 !== 0) {
        callback(new Error('请输入正整数'))
      } else if (num < minAmount) {
        callback(new Error(`最低充值金额为${minAmount}`))
      } else {
        callback()
      }
    },
    trigger: ['blur'],
  },
]

const handlePriceInput = (value: string) => {
  // 过滤非数字字符
  let filtered = value.replace(/[^\d]/g, '')

  // 转换为数值（如果为空字符串，则保持为空，不强制设置最小值）
  if (filtered !== '') {
    // 只保留数字，不强制应用最小值限制
    // 由验证规则在失去焦点时处理最小值检查
    priceForm.value.price = filtered

    // 更新自定义充值名称
    updateCustomName(parseInt(filtered, 10))
  } else {
    // 如果完全为空，将价格设为空字符串
    priceForm.value.price = ''
  }
}
const priceFormRef = ref<InstanceType<typeof ElForm> | null>(null)
const selectedItem = ref<IntegralRechargeItem | null>(null)

// 创建一个引用来访问输入框元素
const inputRef = ref<HTMLInputElement | null>(null)

// 添加一个方法来重置表单验证状态
const resetPriceFormValidation = () => {
  // 由于clearValidate可能不存在或不适用，我们使用更可靠的方式
  // 1. 重置表单数据到正确的值
  if (customPack.value) {
    priceForm.value.price = String(customPack.value.minPurchaseCount)
    updateCustomName(customPack.value.minPurchaseCount)
  }

  // 2. 使用nextTick确保DOM更新后再尝试隐藏或移除验证消息
  nextTick(() => {
    try {
      const errorMessages = document.querySelectorAll('.el-form-item__error')
      errorMessages.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.display = 'none'
        }
      })
    } catch (e) {
      console.error('隐藏错误消息失败:', e)
    }
  })
}

const handleRecharge = (item: IntegralRechargeItem) => {
  const previousItem = selectedItem.value
  selectedItem.value = item

  // 如果是从其他选项切换到自定义套餐
  if (item.unit) {
    // 如果选择的是自定义套餐，设置最小购买金额和更新名称
    if (priceForm.value.price < item.minPurchaseCount) {
      priceForm.value.price = String(item.minPurchaseCount)
      updateCustomName(item.minPurchaseCount)
    }

    // 使用nextTick确保DOM更新后再聚焦
    nextTick(() => {
      // 找到输入框元素并聚焦
      const inputElement = document.querySelector('.theme-color-input input')
      if (inputElement) {
        ;(inputElement as HTMLInputElement).focus()
      }
    })
  }
  // 如果是从自定义套餐切换到其他选项
  else if (previousItem?.unit) {
    // 使用专门的方法重置表单验证
    resetPriceFormValidation()
  }
}
const getIntegralRecharge = async () => {
  try {
    const res = await integralApi.getIntegralRecharge()
    const integralList = res.data.filter((item) => item.type == 0)
    const customListData = res.data.filter((item) => item.type == 1)
    integralRecharge.value = integralList || []
    customList.value = customListData.map((item) => {
      const otherItem = JSON.parse(item.description)
      if (item.products[0].product === CustomType.CUSTOM_LITE) {
        return {
          ...item,
          icon: CustomIcon1,
          name: item.name,
          price: item.price,
          originalPrice: otherItem.originalPrice,
          features: otherItem.features,
        }
      }
      if (item.products[0].product === CustomType.CUSTOM_HQ) {
        return {
          ...item,
          icon: CustomIcon2,
          name: item.name,
          price: item.price,
          originalPrice: otherItem.originalPrice,
          features: otherItem.features,
        }
      }
      if (item.products[0].product === CustomType.CUSTOM_MASTER) {
        return {
          ...item,
          icon: CustomIcon3,
          name: item.name,
          price: item.price,
          originalPrice: otherItem.originalPrice,
          features: otherItem.features,
        }
      }
      if (item.products[0].product === CustomType.CUSTOM_FILMIC) {
        return {
          ...item,
          icon: CustomIcon4,
          name: item.name,
          price: item.price,
          originalPrice: otherItem.originalPrice,
          features: otherItem.features,
        }
      }
    })

    selectedItem.value = integralRecharge.value[0]
    console.log(customList.value, 'customList')
    // 查找 unit 为 true 的第一个套餐，并设置为自定义套餐
    customPack.value = integralRecharge.value.find((item) => item.unit === true) || null
    if (customPack.value) {
      // 初始化自定义套餐信息
      priceForm.value.price = String(customPack.value.minPurchaseCount)
      customName.value = customPack.value.name
      countRatio.value = customPack.value.products[0].count
    }
  } catch (error) {
    console.error('获取积分充值列表失败:', error)
  }
}

const handlePay = debounce(async () => {
  // 如果是自定义充值，先进行表单验证
  if (selectedItem.value?.unit) {
    // 验证自定义充值表单
    let isValid = true

    // 获取最低充值金额
    const minAmount = customPack.value?.minPurchaseCount || 10
    const inputValue = Number(priceForm.value.price)

    // 检查输入是否为有效数字
    if (isNaN(inputValue) || inputValue <= 0 || inputValue % 1 !== 0) {
      message.error('请输入正整数金额')
      isValid = false
    }
    // 检查是否满足最低充值金额
    else if (inputValue < minAmount) {
      message.error(`最低充值金额为${minAmount}元`)
      isValid = false
    }

    // 如果验证不通过，直接返回不继续执行
    if (!isValid) {
      return
    }
  }

  loading.value = true

  try {
    let packId = selectedItem.value?.packId || ''
    let purchaseCount = selectedItem.value?.minPurchaseCount || 1
    let price = selectedItem.value?.price || 0

    // 处理自定义充值
    if (selectedItem.value?.unit) {
      // 对于自定义充值，使用输入的金额作为购买数量
      purchaseCount = Number(priceForm.value.price)
      // 实时计算价格
      price = purchaseCount
    }

    payInfo.value = {
      packId,
      purchaseCount,
      channel: PayChannel.WEIXIN,
      qrCode: '',
      orderId: '',
      price,
    }

    const res = await userApi.createOrder({
      packId,
      purchaseCount,
      channel: PayChannel.WEIXIN,
    })
    if (res.statusCode === Code.SUCCESS.statusCode) {
      const payData = res.data
      payInfo.value.qrCode = payData.qrCode
      payInfo.value.orderId = payData.orderId
      payDialogVisible.value = true
    } else {
      message.error(res.message || '创建订单失败')
    }
  } catch (error) {
    console.error('创建订单失败:', error)
    message.error('创建订单失败，请稍后重试')
  } finally {
    loading.value = false
  }
}, 500)

onMounted(async () => {
  await getIntegralRecharge()
})

const handleClose = () => {
  dialogRechargeVisible.value = false
  emit('update:visible', false)
  emit('close')
}

const handleOrderCustom = debounce(async (item: any) => {
  console.log('订购套餐:', item)
  loading.value = true

  try {
    let packId = item.packId || ''
    let purchaseCount = item.minPurchaseCount || 1
    let price = item.price || 0

    payInfo.value = {
      packId,
      purchaseCount,
      channel: PayChannel.WEIXIN,
      qrCode: '',
      orderId: '',
      price,
    }

    const res = await userApi.createOrder({
      packId,
      purchaseCount,
      channel: PayChannel.WEIXIN,
    })
    if (res.statusCode === Code.SUCCESS.statusCode) {
      const payData = res.data
      payInfo.value.qrCode = payData.qrCode
      payInfo.value.orderId = payData.orderId
      payDialogVisible.value = true
    } else {
      message.error(res.message || '创建订单失败')
    }
  } catch (error) {
    console.error('创建订单失败:', error)
    message.error('创建订单失败，请稍后重试')
  } finally {
    loading.value = false
  }
}, 500)

watch(
  () => props.visible,
  (newVal) => {
    dialogRechargeVisible.value = newVal
  },
)
</script>

<template>
  <Transition name="dialog-fade">
    <div v-if="dialogRechargeVisible" class="dialog-wrapper">
      <div class="dialog-content rounded-[15px]">
        <div
          class="absolute top-[-82px] right-[-100px] w-[236px] h-[256px] z-30 pointer-events-none"
        >
          <img src="@/assets/img/recharge_right.png" alt="bg" class="w-full h-full" />
        </div>
        <!-- 关闭按钮 -->
        <div
          class="absolute top-[-50px] left-[-50px] w-full h-[50px] z-10 flex items-center justify-start"
        >
          <img
            src="@/assets/icon/close.png"
            alt="bg"
            class="w-[50px] h-[50px] hover-transition"
            @click="handleClose"
          />
        </div>
        <!-- 充值内容 -->
        <div
          class="flex flex-col w-[800px] relative rounded-[15px] overflow-hidden"
          style="background: linear-gradient(182deg, #1767dd 2%, #4b9fee 48%, #81daff 96%)"
        >
          <div class="flex h-[120px] w-full items-center justify-between relative">
            <div class="absolute top-0 right-[120px] w-[290px] h-[143px] pointer-events-none">
              <img src="@/assets/icon/recharge_gif.gif" alt="bg" class="w-full h-full" />
            </div>
            <div class="flex items-center ml-[30px]">
              <div class="flex flex-col mr-4">
                <img
                  :src="avatarUrl"
                  alt="user"
                  class="avatar-img w-[50px] h-[50px] rounded-[50%]"
                />
              </div>
              <div class="flex flex-col">
                <div class="text-size16 font-[500] text-[#F6F9FA]">充值账号：</div>
                <div class="text-size16 font-[600] text-[#F6F9FA]">
                  {{ useUserStoreHook()?.username }}
                </div>
                <div class="flex items-center">
                  <div class="text-size14 font-[400] text-[#F6F9FA]">
                    积分余额: {{ useUserStoreHook()?.scoreDetail.credit }}积分
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col h-full">
              <div class="flex flex-row items-center justify-center relative z-10 flex-1 mr-[40px]">
                <div class="text-size20 font-[700] text-[#F6F9FA]">aimusic灵镜</div>
                <div class="text-size20 font-[400] text-[#F6F9FA] ml-[12px]">让创作如呼吸自然</div>
              </div>
              <div class="h-[40px] flex items-end justify-end">
                <div class="flex items-center tab-container relative">
                  <!-- 滑动指示器 -->
                  <div
                    class="tab-indicator absolute top-0 h-[40px] bg-gradient-to-r from-[#f6f9fa] to-[#e6f6ff] transition-all duration-500 ease-out"
                    :style="{
                      width: '266px',
                      left: payType === 1 ? '0px' : '236px',
                      clipPath:
                        payType === 1
                          ? 'polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%)'
                          : 'polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%)',
                    }"
                  />
                  <div
                    class="tab-item w-[266px] h-[40px] flex items-center justify-center cursor-pointer transition-all duration-300 relative"
                    :class="payType === 1 ? 'tab-active' : 'tab-inactive'"
                    :style="{
                      clipPath: 'polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%)',
                      marginRight: '-30px',
                      zIndex: payType === 1 ? 20 : 10,
                    }"
                    @click="changePayType(1)"
                  >
                    <span class="tab-text transition-all duration-300">积分充值</span>
                  </div>
                  <div
                    class="tab-item w-[266px] h-[40px] flex items-center justify-center cursor-pointer transition-all duration-300 relative"
                    :class="payType === 2 ? 'tab-active' : 'tab-inactive'"
                    :style="{
                      clipPath: 'polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%)',
                      zIndex: payType === 2 ? 20 : 10,
                    }"
                    @click="changePayType(2)"
                  >
                    <span class="tab-text transition-all duration-300">高级定制</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Transition
            name="content-slide"
            mode="out-in"
            appear
            :duration="{ enter: 400, leave: 300 }"
          >
            <div
              v-if="payType === 2"
              key="custom"
              class="recharge-content flex flex-row pl-[40px] pr-[47px] pt-[34px] pb-[37px] h-[496px]"
              v-loading="loading"
            >
              <CustomList :custom-list="customList" @handleOrder="handleOrderCustom" />
            </div>
            <div
              v-else-if="payType === 1"
              key="integral"
              class="recharge-content flex flex-row pl-[40px] pr-[47px] pt-[34px] pb-[37px] h-[496px]"
            >
              <!-- 侧边套餐栏 -->
              <IntegardlSide />
              <!-- 充值内容 充值类型-->
              <div v-loading="loading" class="flex flex-col flex-1">
                <div class="text-size14 text-[#7C7777] mb-[13px]">积分充值</div>

                <div
                  class="flex flex-row items-center flex-wrap w-full max-h-[340px] overflow-y-auto scrollbar scrollbar-left"
                >
                  <!-- 充值列表 -->
                  <div
                    v-for="(item, index) in integralRecharge"
                    :key="item.packId"
                    :class="[
                      'flex flex-col border-[#E5E5EA] border-solid border-[1px] rounded-[5px] bg-[#F7F7FC] h-[82px] mb-[22px]  hover-animate relative',
                      'w-[calc((100%-18px)/2)]',
                      (index + 1) % 2 !== 0 ? 'mr-[18px]' : '',

                      selectedItem?.packId === item.packId ? '!border-[#0770ED] !bg-[#E0F3FF]' : '',
                    ]"
                    @click="handleRecharge(item)"
                  >
                    <template v-if="!item.unit">
                      <div class="flex items-center justify-start w-full mt-[17px]">
                        <img
                          v-if="selectedItem?.packId === item.packId"
                          src="@/assets/icon/integral_a.png"
                          alt="bg"
                          class="w-[26px] h-[26px] ml-[21px]"
                        />
                        <img
                          v-else
                          src="@/assets/icon/integral.png"
                          alt="bg"
                          class="w-[26px] h-[26px] ml-[21px]"
                        />
                        <div
                          class="text-size20 font-bold text-[#000000] ml-[6px]"
                          :class="{ 'text-[#1C6CDF]': selectedItem?.packId === item.packId }"
                        >
                          {{ item.name }}
                        </div>
                      </div>
                      <div class="flex items-center justify-between w-full mt-[-4px]">
                        <div class="text-[#8E8E93] ml-[53px]">
                          <span
                            class="text-size12"
                            :class="{ 'text-[#1C6CDF]': selectedItem?.packId === item.packId }"
                            >￥</span
                          >
                          <span
                            class="text-size16"
                            :class="{ 'text-[#1C6CDF]': selectedItem?.packId === item.packId }"
                            >{{ item.price }}</span
                          >
                        </div>
                        <img
                          v-if="selectedItem?.packId === item.packId"
                          src="@/assets/icon/select.png"
                          alt="bg"
                          class="w-[18px] h-[18px] mr-[15px]"
                        />
                      </div>
                    </template>
                    <!-- 自定义充值 -->
                    <template v-else>
                      <div
                        class="text-size12 bg-[#AEAEB2] absolute top-0 right-0 rounded-[0px_5px_0px_5px] flex items-center justify-center w-[100px] h-[16px] text-[#FFFFFF]"
                        :class="{ 'bg-[#1C6CDF]': selectedItem?.packId === item.packId }"
                      >
                        自定义充值
                      </div>
                      <div class="flex items-center justify-start w-full mt-[17px]">
                        <img
                          v-if="selectedItem?.packId === item.packId"
                          src="@/assets/icon/integral_a.png"
                          alt="bg"
                          class="w-[26px] h-[26px] ml-[21px]"
                        />
                        <img
                          v-else
                          src="@/assets/icon/integral.png"
                          alt="bg"
                          class="w-[26px] h-[26px] ml-[21px]"
                        />
                        <div
                          class="text-size20 font-bold text-[#000000] ml-[6px]"
                          :class="{ 'text-[#1C6CDF]': selectedItem?.packId === item.packId }"
                        >
                          {{
                            selectedItem?.packId === item.packId && customName
                              ? customName
                              : item.name
                          }}
                        </div>
                      </div>
                      <div class="flex items-center justify-between w-full mt-[-4px]">
                        <div class="text-[#8E8E93] ml-[28px] flex items-center">
                          <span
                            class="text-size12"
                            :class="{ 'text-[#1C6CDF]': selectedItem?.packId === item.packId }"
                            >￥</span
                          >
                          <el-form ref="priceFormRef" :model="priceForm" class="custom-form">
                            <el-form-item prop="price" :rules="rulesLocalPrice">
                              <el-input
                                v-model="priceForm.price"
                                :placeholder="`最低充值金额${item.minPurchaseCount}`"
                                class="custom-input !text-size16"
                                :class="{
                                  'theme-color-input': selectedItem?.packId === item.packId,
                                }"
                                type="number"
                                @input="handlePriceInput"
                                @focus="
                                  () => {
                                    if (selectedItem?.packId !== item.packId) handleRecharge(item)
                                  }
                                "
                              />
                            </el-form-item>
                          </el-form>
                        </div>
                        <img
                          v-if="selectedItem?.packId === item.packId"
                          src="@/assets/icon/select.png"
                          alt="bg"
                          class="w-[18px] h-[18px] mr-[15px]"
                        />
                      </div>
                    </template>
                  </div>
                </div>

                <!-- 立即购买 -->
                <div class="flex w-full items-center justify-center">
                  <div
                    class="font-roboto px-[50px] py-[7px] bg-home-aisound-btn rounded-[25px] text-size16 text-white mt-[30px] hover-transition"
                    @click="handlePay"
                  >
                    立即购买
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
      <PayDialog v-model:visible="payDialogVisible" :pay-info="payInfo" />
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.dialog-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-content {
  position: relative;
  animation: dialog-appear 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  transform-origin: center center;
  will-change: transform, opacity;
  backface-visibility: hidden;
}

@keyframes dialog-appear {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.96);
    filter: blur(3px);
  }
  20% {
    // opacity: 0.5;
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

.recharge-content {
  background: linear-gradient(119deg, #f6f9fa 1%, #e6f6ff 67%, #6dc4f9 99%);
  border-radius: 15px 0px 15px 15px;
}
// 动画效果
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: all 0.8s cubic-bezier(0.19, 1, 0.22, 1);
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
  transform: scale(0.92);
  filter: blur(2px);
}

.dialog-fade-enter-to,
.dialog-fade-leave-from {
  opacity: 1;
  transform: scale(1);
  filter: blur(0);
}

.custom-form {
  :deep(.el-form-item) {
    margin: 0;
    padding: 0;

    .el-form-item__content {
      margin: 0 !important;
    }
  }

  :deep(.el-input) {
    .el-input__wrapper {
      box-shadow: none !important;
      padding: 0;
      background: none;
      border-bottom: 1px solid #d0d0d0;
      border-radius: 0;
      transition: all 0.3s ease;

      &:hover,
      &:focus-within {
        border-bottom-color: #0770ed;
      }
    }

    .el-input__inner {
      border: none;
      padding: 0 0 2px 0;
      height: 24px;
      line-height: 24px;
      font-size: 16px !important;
      width: 120px;
      color: #8e8e93;
      transition: color 0.3s ease;

      &::placeholder {
        color: #8e8e93;
        font-size: 16px !important;
      }

      /* 隐藏number类型输入框的上下箭头 - Chrome, Safari, Edge, Opera */
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* 隐藏number类型输入框的上下箭头 - Firefox */
      -moz-appearance: textfield;
    }
  }

  // 主题色输入框样式
  :deep(.theme-color-input) {
    .el-input__wrapper {
      border-bottom-color: #0770ed;
    }

    .el-input__inner {
      color: #0770ed;
      font-weight: 500;
    }
  }

  // 未选中时隐藏下边框
  :deep(.el-input) {
    .el-input__wrapper {
      border-bottom: none;
    }
  }

  // 只在选中自定义项时显示下边框
  :deep(.theme-color-input) {
    .el-input__wrapper {
      border-bottom: 1px solid #0770ed;
    }
  }
}

// 选中状态下的输入框文字颜色
.custom-form:has(.el-form-item) {
  :deep(.el-input) {
    .active-input .el-input__inner {
      color: #0770ed;
    }
  }
}

// 当父元素选中时更改输入框文字颜色
:deep(.el-input) {
  .el-input__inner {
    .is-selected & {
      color: #0770ed !important;
    }
  }
}

.hover-animate {
  transition: all 0.3s ease;
  &:hover {
    transform: scale(1.01);
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -2px rgba(0, 0, 0, 0.1);
  }
}

// 添加新的卡片切换动画和悬停效果
.recharge-card {
  // 基础过渡效果
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  overflow: hidden;
  position: relative;
  cursor: pointer;

  // 鼠标悬停效果
  &:hover {
    transform: translateY(-5px);
    border-color: #0770ed !important;
    box-shadow: 0 10px 15px -3px rgba(7, 112, 237, 0.1);

    // 添加一个微妙的光泽效果
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 50%;
      height: 100%;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transform: skewX(-25deg);
      animation: shine 1.2s;
    }
  }

  // 选中状态的过渡效果
  &:active {
    transform: translateY(0);
    transition-duration: 0.1s;
  }
}

// 光泽动画
@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

// Tab切换样式
.tab-container {
  .tab-item {
    font-size: 16px;
    font-weight: 500;
    position: relative;
    // margin-left: -15px; // 让tab重叠，形成梯形效果

    &:first-child {
      margin-left: 0; // 第一个tab不需要左边距
    }

    &.tab-active {
      // background: rgba(255, 255, 255, 0.95);
      background: linear-gradient(119deg, #f6f9fa 1%, #e6f6ff 67%, #6dc4f9 99%);
      z-index: 20; // 确保激活的tab在最上层
      .tab-text {
        background: linear-gradient(72deg, #0770ed 0%, #47cdf3 103%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        text-fill-color: transparent;
        font-weight: 600;
      }
    }

    &.tab-inactive {
      // background: rgba(255, 255, 255, 0.6);
      background: #fff;
      z-index: 10;

      .tab-text {
        color: #adadad;
        font-weight: 500;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.8);
        .tab-text {
          color: #666;
        }
      }
    }
  }
}

// 内容切换动画
.content-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.content-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

.content-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.content-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.content-slide-enter-to,
.content-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}

// 确保内容容器有稳定的布局
.recharge-content {
  will-change: transform, opacity, height;
  backface-visibility: hidden;
  transform: translateZ(0);
}

// Tab指示器动画增强
.tab-indicator {
  z-index: 5; // 确保指示器在非激活tab之上，但在激活tab之下

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 70%
    );
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
}
</style>
